import React, {useCallback, useMemo, useRef, useState} from 'react'
import AvatarEditor from 'react-avatar-editor'
import {domToBlob} from 'modern-screenshot'
import styled from 'styled-components'
import exifr from 'exifr'
import {useTranslation} from 'i18n'
import {
  FORKYGRAM_CONVERTED_SPECIAL_IMAGE_UPLOAD_TYPE,
  FORKYGRAM_SPECIAL_IMAGE_UPLOAD_TYPE,
  FORKYGRAM_UPLOAD_MAX_IMAGE_SIZE,
  FORKYGRAM_UPLOAD_MAX_IMAGE_SIZE_MB,
  FORKYGRAM_UPLOAD_MAX_RESOLUTION,
  FORKYGRAM_UPLOAD_MIN_IMAGE_RESOLUTION,
  FORKYGRAM_UPLOAD_TYPE,
  GIFT_SHOP_UPLOAD_TYPE,
} from 'consts'
import {
  ForkygramUploadThumbnailData,
  ImageExif,
  LayoutView,
  WindowModeType,
} from 'types'
import {useWindowMode} from 'windows'
import {blobToFile, cropImage, getAndSupplyFileMime, showSnackbar} from 'utils'
import {
  Button,
  Icon,
  Image,
  ImageCropper,
  Modal,
  Paragraph,
  TabView,
  View,
} from 'common/components'
import convertUnit from 'lib/unit'
import {convertImage} from 'utils/lib/heic2any/heic2anyHelper'

interface WindowModeProps {
  mode: WindowModeType
}
interface ThumbnailProps {
  height: number
}

const StyledModal = styled(Modal)<WindowModeProps>`
  width: 100%;
  ${({mode}) => ({
    maxWidth: mode === 'website' ? convertUnit(600) : undefined,
    height: mode === 'website' ? '90%' : '100%',
    borderRadius: mode === 'website' ? undefined : 0,
  })}
  overflow-y: scroll;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    width: 0;
  }
`
const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`
const StyledContentContainer = styled(View)`
  display: flex;
  height: 100%;
  background-color: ${({theme}) => theme.white_3};
`
const StyledUploadThumbnailContainer = styled.div`
  padding: ${convertUnit(20)};
  margin: ${convertUnit(20)};
  border: ${convertUnit(2)} dashed ${({theme}) => theme.gray_1};
  border-radius: ${convertUnit(16)};
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${convertUnit(8)};
`
const StyledButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: fill-available;
  padding: ${convertUnit(20)};
  border-top: ${convertUnit(1)} solid ${({theme}) => theme.white_5};
  gap: ${convertUnit(20)};
`
const StyledButton = styled(Button)`
  width: fill-available;
`
const StyledValidationContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(8)};
  text-align: left;
  align-items: flex-start;
`
const StyledHeader = styled.div<WindowModeProps>`
  ${({mode}) => ({
    flexDirection: mode === 'website' ? 'row' : 'row-reverse',
    justifyContent: mode === 'website' ? 'space-between' : 'flex-end',
  })}
  display: flex;
  padding: ${convertUnit(8)} ${convertUnit(20)} ${convertUnit(4)};
  gap: ${convertUnit(12)};
  align-items: center;
`
const StyledUploadInput = styled.input`
  width: 0;
  height: 0;
  overflow: hidden;
`
const StyledSelectedThumbnailContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: fill-available;
  margin: 0 ${convertUnit(20)};
  gap: ${convertUnit(8)};
`
const StyledImage = styled(Image)`
  object-fit: scale-down;
  width: 100%;
  aspect-ratio: 1;
  max-width: ${convertUnit(300)};
`
const StyledVideoContainer = styled.div<ThumbnailProps>`
  position: relative;
  overflow: hidden;
  border-radius: inherit;
  height: ${({height}) => convertUnit(height)};
  width: 100%;
  display: flex;
  align-items: center;
  align-self: center;
  justify-content: center;
`
const StyledVideo = styled.video`
  height: inherit;
  width: inherit;
`

interface ForkygramTemplateEditThumbnailVideoProps {
  toggleModal(): void
  onSave(thumbnail: string, metadata?: ForkygramUploadThumbnailData): void
  videoSource: string
}

export default function ForkygramTemplateEditThumbnailVideo({
  toggleModal,
  onSave,
  videoSource,
}: ForkygramTemplateEditThumbnailVideoProps) {
  const {translate} = useTranslation()
  const mode = useWindowMode()
  const [layout, setLayout] = useState<LayoutView>()
  type EditThumbnailType = 'select' | 'upload'
  const stateType = useState<EditThumbnailType | undefined>('select')
  const uploadRef = useRef<HTMLInputElement>(null)
  const [src, setSrc] = useState<string | undefined>()
  const [selectedImage, setSelectedImage] = useState<File | undefined>()
  const [showCropper, setShowCropper] = useState(false)
  const canvas = useRef<AvatarEditor>(null)
  const [thumbnailMetadata, setThumbnailMetadata] = useState<
    ForkygramUploadThumbnailData | undefined
  >()
  const videoRef = useRef<HTMLVideoElement>(null)
  const [selectedThumbnail, setSelectedThumbnail] = useState<
    string | undefined
  >()

  const handleRenderTabTitle = useCallback(
    (key: 'select' | 'upload') =>
      translate('forkygram:uploadEditThumbnail', {context: key}),
    [translate],
  )

  const handleRenderUploadThumbnailValidation = useMemo(
    () =>
      Array.from({length: 3}).map((_, idx) => (
        <StyledValidationContentContainer>
          <Paragraph fontSize="m" color="primary_5">{`\u2022`}</Paragraph>
          <Paragraph fontSize="m" color="primary_5">
            {translate('forkygram:uploadEditThumbnailUploadValidation', {
              context: `${idx + 1}`,
              size: FORKYGRAM_UPLOAD_MAX_IMAGE_SIZE_MB,
              width: FORKYGRAM_UPLOAD_MIN_IMAGE_RESOLUTION,
              height: FORKYGRAM_UPLOAD_MIN_IMAGE_RESOLUTION,
            })}
          </Paragraph>
        </StyledValidationContentContainer>
      )),
    [translate],
  )

  const handleCrop = useCallback(
    async (file?: File) => {
      if (file) {
        const img = file
        const exif = (await exifr.parse(img)) as ImageExif
        const {size} = img
        const reader = new FileReader()
        reader.onload = async () => {
          const url = reader.result?.toString()
          const image = document.createElement('img')

          if (!url) return
          image.src = url

          image.onload = () => {
            const {width, height} = image

            if (width * height > FORKYGRAM_UPLOAD_MAX_RESOLUTION) {
              showSnackbar(
                translate('forkygram:uploadMaxImageResolutionMessage'),
              )
              return
            }
            if (
              width < FORKYGRAM_UPLOAD_MIN_IMAGE_RESOLUTION ||
              height < FORKYGRAM_UPLOAD_MIN_IMAGE_RESOLUTION
            ) {
              showSnackbar(
                translate('forkygram:uploadMinImageResolutionMessage'),
              )
              return
            }
            if (stateType[0] === 'select') {
              setSelectedThumbnail(url)
            } else {
              setSrc(url)
            }
            setThumbnailMetadata(
              (prev) =>
                prev && {
                  ...prev,
                  height,
                  width,
                  size,
                  exif: JSON.stringify(exif),
                  file,
                },
            )
            setShowCropper(false)
            setSelectedImage(undefined)
          }
        }

        reader.readAsDataURL(img)
      }
    },
    [stateType, translate],
  )

  const handleCheckImage = useCallback(() => {
    if (uploadRef.current?.files?.length) {
      let img = uploadRef.current.files[0]
      const {size, name} = img

      let type = getAndSupplyFileMime(GIFT_SHOP_UPLOAD_TYPE, img)

      const handlingSpecialImageType = FORKYGRAM_SPECIAL_IMAGE_UPLOAD_TYPE.includes(
        type,
      )

      if (!FORKYGRAM_UPLOAD_TYPE.includes(type)) {
        showSnackbar(translate('forkygram:uploadInvalidImageFormat'))
        return
      }
      if (size > FORKYGRAM_UPLOAD_MAX_IMAGE_SIZE) {
        showSnackbar(translate('forkygram:uploadMaxImageSizeMessage'))
        return
      }

      const reader = new FileReader()
      reader.onload = async () => {
        const url = reader.result?.toString()

        if (!url) return

        if (
          url?.includes('image') ||
          (type.includes('image') && handlingSpecialImageType)
        ) {
          const image = document.createElement('img')

          if (handlingSpecialImageType) {
            type = FORKYGRAM_CONVERTED_SPECIAL_IMAGE_UPLOAD_TYPE

            const {file: newImage, blob} = await convertImage(
              url,
              FORKYGRAM_CONVERTED_SPECIAL_IMAGE_UPLOAD_TYPE,
              name,
            )

            image.src = URL.createObjectURL(blob)
            img = newImage
          } else {
            image.src = url
          }

          image.onload = () => {
            const {width, height} = image

            if (width * height > FORKYGRAM_UPLOAD_MAX_RESOLUTION) {
              showSnackbar(
                translate('forkygram:uploadMaxImageResolutionMessage'),
              )
              return
            }
            if (
              width < FORKYGRAM_UPLOAD_MIN_IMAGE_RESOLUTION ||
              height < FORKYGRAM_UPLOAD_MIN_IMAGE_RESOLUTION
            ) {
              showSnackbar(
                translate('forkygram:uploadMinImageResolutionMessage'),
              )
              return
            }
            setThumbnailMetadata({
              extension: type.split('/')[1],
              height,
              width,
              size,
              exif: '',
              file: undefined,
            })
            setSelectedImage(img)
            setShowCropper(true)
          }
        }
      }
      reader.readAsDataURL(img)
    }
  }, [translate])

  const handleScaleFrame = useCallback(() => {
    if (videoRef.current === null) return {width: 0, height: 0}

    if (
      videoRef.current.videoHeight >= FORKYGRAM_UPLOAD_MIN_IMAGE_RESOLUTION &&
      videoRef.current.videoWidth >= FORKYGRAM_UPLOAD_MIN_IMAGE_RESOLUTION
    ) {
      // no need to scale
      return {
        width: videoRef.current.videoWidth,
        height: videoRef.current.videoHeight,
      }
    }
    const ratio = videoRef.current.videoWidth / videoRef.current.videoHeight
    let adjustedHeight = videoRef.current.videoHeight
    let adjustedWidth = videoRef.current.videoWidth
    if (videoRef.current.videoWidth < videoRef.current.videoHeight) {
      // handle portrait, aspect ratio < 1
      adjustedWidth = FORKYGRAM_UPLOAD_MIN_IMAGE_RESOLUTION
      adjustedHeight = adjustedWidth / ratio
    } else {
      // handle square or landscape, aspect ratio >= 1
      adjustedHeight = FORKYGRAM_UPLOAD_MIN_IMAGE_RESOLUTION
      adjustedWidth = ratio * adjustedHeight
    }
    return {width: adjustedWidth, height: adjustedHeight}
  }, [])

  const captureFrame = useCallback(async () => {
    if (videoRef.current === null) return
    const {width: adjustedWidth, height: adjustedHeight} = handleScaleFrame()

    const blob = await domToBlob(videoRef.current, {
      quality: 1,
      width: adjustedWidth,
      height: adjustedHeight,
    })
    setThumbnailMetadata({
      extension: blob.type.split('/')[1],
      size: blob.size,
      height: adjustedHeight,
      width: adjustedWidth,
      exif: '',
      file: undefined,
    })
    const file = blobToFile(blob, 'thumbnail', new Date().getTime())
    setSelectedImage(file)
    setShowCropper(true)
  }, [handleScaleFrame])

  const handleRenderInputFile = useMemo(
    () => (
      <StyledUploadInput
        type="file"
        ref={uploadRef}
        onChange={handleCheckImage}
        accept={GIFT_SHOP_UPLOAD_TYPE.join(',')}
      />
    ),
    [handleCheckImage],
  )

  const handleRenderUploadThumbnail = useMemo(
    () => (
      <StyledContentContainer>
        {src ? (
          <StyledSelectedThumbnailContainer>
            <StyledImage src={src} alt="" />
            <StyledButton
              label={translate('forkygram:uploadEditThumbnailUploadNew')}
              leftIcon={<Icon type="image" color="primary_5" size={16} />}
              color="primary_5"
              backgroundColor="white_3"
              onClick={() => uploadRef.current?.click()}
            />
          </StyledSelectedThumbnailContainer>
        ) : (
          <StyledUploadThumbnailContainer>
            <Icon type="upload" size={32} color="primary_5" />
            <div>{handleRenderUploadThumbnailValidation}</div>
            <Button
              leftIcon={<Icon type="image" color="white_1" size={16} />}
              label={translate('giftShop:selectFile')}
              style={{width: 'fit-content'}}
              onClick={() => uploadRef.current?.click()}
            />
          </StyledUploadThumbnailContainer>
        )}
      </StyledContentContainer>
    ),
    [handleRenderUploadThumbnailValidation, src, translate],
  )

  const handleRenderSelectThumbnail = useMemo(
    () => (
      <StyledContentContainer onLayout={setLayout}>
        {selectedThumbnail ? (
          <StyledSelectedThumbnailContainer>
            <StyledImage src={selectedThumbnail} alt="" />
          </StyledSelectedThumbnailContainer>
        ) : layout ? (
          <StyledVideoContainer
            height={
              mode === 'website'
                ? layout.clientHeight - 10
                : layout.clientHeight - 5
            }>
            <StyledVideo src={videoSource} controls ref={videoRef} muted />
          </StyledVideoContainer>
        ) : (
          <></>
        )}
      </StyledContentContainer>
    ),
    [layout, mode, selectedThumbnail, videoSource],
  )

  const handleRenderTabItem = useCallback(
    (key: EditThumbnailType) => {
      switch (key) {
        case 'select':
          return handleRenderSelectThumbnail
        case 'upload':
          return handleRenderUploadThumbnail
        default:
          return <></>
      }
    },
    [handleRenderSelectThumbnail, handleRenderUploadThumbnail],
  )

  return showCropper && selectedImage ? (
    <ImageCropper
      visible={showCropper}
      file={selectedImage}
      componentRef={canvas}
      borderRadius={0}
      onBackdropPress={() => {
        setSelectedImage(undefined)
        setShowCropper(false)
      }}
      onClickSave={() => {
        cropImage(canvas, 'thumbnail', (res) => handleCrop(res), true)
      }}
    />
  ) : (
    <StyledModal visible mode={mode} onBackdropPress={toggleModal}>
      <StyledContainer>
        {handleRenderInputFile}
        <StyledHeader mode={mode}>
          <Paragraph fontSize="l" fontWeight="medium">
            {translate('forkygram:uploadEditThumbnail')}
          </Paragraph>
          <Icon
            type="close"
            color="primary_5"
            className="pressable"
            onClick={toggleModal}
          />
        </StyledHeader>
        <TabView
          routes={['select', 'upload']}
          stateSelect={stateType}
          renderTitle={handleRenderTabTitle}
          renderItem={handleRenderTabItem}
          containerStyle={{height: '100%'}}
        />
        <StyledButtonContainer>
          <StyledButton
            label={translate('global:cancel')}
            color="primary_5"
            backgroundColor="white_3"
            onClick={toggleModal}
          />
          <StyledButton
            label={translate(
              stateType[0] === 'select' && !selectedThumbnail
                ? 'giftShop:choosePicture'
                : 'global:save',
            )}
            onClick={() => {
              if (stateType[0] === 'select') {
                if (!selectedThumbnail) {
                  captureFrame()
                } else {
                  selectedThumbnail &&
                    onSave(selectedThumbnail, thumbnailMetadata)
                  toggleModal()
                }
              } else {
                src && onSave(src, thumbnailMetadata)
                toggleModal()
              }
            }}
          />
        </StyledButtonContainer>
      </StyledContainer>
    </StyledModal>
  )
}
